import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Heading from "@tiptap/extension-heading";
import CodeBlock from "@tiptap/extension-code-block";
import Blockquote from "@tiptap/extension-blockquote";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import { ResizableImage } from "./ResizableImageExtension";
import {
  Table,
  TableRow,
  TableCell,
  TableHeader,
} from "@tiptap/extension-table";
import {
  ySyncPlugin,
  yCursorPlugin,
  yUndoPlugin,
  undo,
  redo,
} from "y-prosemirror";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { cn, generateUUID } from "@/lib/utils";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Code,
  CheckSquare,
  Minus,
  Strikethrough,
  Loader2,
  TableIcon,
  Columns,
  Rows,
  Plus,
  Trash2,
  ImageIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { usePeople } from "@/hooks/usePeople";
import { useCollaboration } from "@/hooks/useCollaboration";
import { Extension } from "@tiptap/core";
import type {
  SuggestionKeyDownProps,
  SuggestionProps,
} from "@tiptap/suggestion";
import { resizeAndUploadImage } from "@/lib/image-upload";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useSlashCommandItems } from "./useSlashCommandItems";
import { createSlashCommandSuggestion } from "./slashCommandSuggestion";
import { ExternalLinkDialog } from "./ExternalLinkDialog";

interface CollaborativeEditorProps {
  documentId: string | null;
  placeholder?: string;
  className?: string;
  editable?: boolean;
  onMentionsChange?: (personIds: string[]) => void;
}

export interface CollaborativeEditorRef {
  getMentionedPersonIds: () => string[];
}

interface MentionItem {
  id: string;
  label: string;
  mentionID: string;
}

type MentionSuggestionProps = {
  lifecycle: SuggestionProps<MentionItem, MentionItem>;
  keyDown: SuggestionKeyDownProps;
};

type SyncStatus = {
  label: string;
  className: string;
};

const StableMention = Mention.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      mentionID: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-mention-id"),
        renderHTML: (attributes) => {
          if (!attributes.mentionID) return {};
          return { "data-mention-id": attributes.mentionID };
        },
      },
    };
  },
});

const MenuBar = ({
  editor,
  onImageUpload,
  canUploadImages,
  syncStatus,
}: {
  editor: Editor | null;
  onImageUpload: () => void;
  canUploadImages: boolean;
  syncStatus: SyncStatus;
}) => {
  if (!editor) return null;

  return (
    <div className="flex items-center gap-0.5 p-2 border-b border-border flex-wrap">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={cn("h-8 w-8 p-0", editor.isActive("bold") && "bg-accent")}
        title="Bold"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={cn("h-8 w-8 p-0", editor.isActive("italic") && "bg-accent")}
        title="Italic"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={cn("h-8 w-8 p-0", editor.isActive("strike") && "bg-accent")}
        title="Strikethrough"
      >
        <Strikethrough className="h-4 w-4" />
      </Button>
      <Separator orientation="vertical" className="mx-1 h-6" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive("heading", { level: 1 }) && "bg-accent",
        )}
        title="Heading 1"
      >
        <Heading1 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive("heading", { level: 2 }) && "bg-accent",
        )}
        title="Heading 2"
      >
        <Heading2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive("heading", { level: 3 }) && "bg-accent",
        )}
        title="Heading 3"
      >
        <Heading3 className="h-4 w-4" />
      </Button>
      <Separator orientation="vertical" className="mx-1 h-6" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive("bulletList") && "bg-accent",
        )}
        title="Bullet list"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive("orderedList") && "bg-accent",
        )}
        title="Numbered list"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleTaskList().run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive("taskList") && "bg-accent",
        )}
        title="Task list"
      >
        <CheckSquare className="h-4 w-4" />
      </Button>
      <Separator orientation="vertical" className="mx-1 h-6" />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive("blockquote") && "bg-accent",
        )}
        title="Quote"
      >
        <Quote className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={cn(
          "h-8 w-8 p-0",
          editor.isActive("codeBlock") && "bg-accent",
        )}
        title="Code block"
      >
        <Code className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Horizontal rule"
      >
        <Minus className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() =>
          editor
            .chain()
            .focus()
            .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
            .run()
        }
        title="Insert table"
      >
        <TableIcon className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onImageUpload}
        disabled={!canUploadImages}
        title={
          canUploadImages ? "Insert image" : "Images require an online sync"
        }
      >
        <ImageIcon className="h-4 w-4" />
      </Button>
      {editor.isActive("table") && (
        <>
          <Separator orientation="vertical" className="mx-1 h-6" />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().addColumnAfter().run()}
            title="Add column"
            className="h-8 px-1.5 gap-0.5 text-xs"
          >
            <Columns className="h-3.5 w-3.5" />
            <Plus className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().deleteColumn().run()}
            title="Remove column"
            className="h-8 px-1.5 gap-0.5 text-xs"
          >
            <Columns className="h-3.5 w-3.5" />
            <Minus className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().addRowAfter().run()}
            title="Add row"
            className="h-8 px-1.5 gap-0.5 text-xs"
          >
            <Rows className="h-3.5 w-3.5" />
            <Plus className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().deleteRow().run()}
            title="Remove row"
            className="h-8 px-1.5 gap-0.5 text-xs"
          >
            <Rows className="h-3.5 w-3.5" />
            <Minus className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().deleteTable().run()}
            title="Delete table"
            className="h-8 px-1.5 gap-0.5 text-xs text-destructive hover:text-destructive"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </>
      )}
      <div
        className={cn(
          "ml-auto rounded border px-2 py-0.5 text-xs font-medium",
          syncStatus.className,
        )}
      >
        {syncStatus.label}
      </div>
    </div>
  );
};

export const CollaborativeEditor = forwardRef<
  CollaborativeEditorRef,
  CollaborativeEditorProps
>(
  (
    {
      documentId,
      placeholder = "Start typing...",
      className,
      editable = true,
      onMentionsChange,
    },
    ref,
  ) => {
    const navigate = useNavigate();
    const { data: people = [] } = usePeople("employee");
    const peopleRef = useRef(people);
    useEffect(() => {
      peopleRef.current = people;
    }, [people]);
    const [mentionedPersonIds, setMentionedPersonIds] = useState<string[]>([]);
    const {
      ydoc,
      provider,
      isSynced,
      isLoading,
      canEdit,
      connectionStatus,
      hasServerConfirmedDocument,
    } = useCollaboration(documentId);
    const canUploadImages = isSynced;
    const canUploadImagesRef = useRef(canUploadImages);
    useEffect(() => {
      canUploadImagesRef.current = canUploadImages;
    }, [canUploadImages]);

    // Slash command state
    const { itemsRef: slashItemsRef } = useSlashCommandItems();
    const [externalLinkDialog, setExternalLinkDialog] = useState<{
      open: boolean;
      position: { top: number; left: number };
    }>({ open: false, position: { top: 0, left: 0 } });
    const editorInstanceRef = useRef<Editor | null>(null);

    const handleExternalLink = useCallback(() => {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        setExternalLinkDialog({
          open: true,
          position: { top: rect.bottom + 4, left: rect.left },
        });
      } else {
        setExternalLinkDialog({
          open: true,
          position: { top: 200, left: 200 },
        });
      }
    }, []);

    const handleExternalLinkSubmit = useCallback(
      (url: string, label: string) => {
        const ed = editorInstanceRef.current;
        if (!ed) return;
        ed.chain()
          .focus()
          .insertContent({
            type: "text",
            text: label,
            marks: [
              {
                type: "link",
                attrs: {
                  href: url,
                  target: "_blank",
                  rel: "noopener noreferrer",
                },
              },
            ],
          })
          .insertContent({ type: "text", text: " " })
          .run();
        setExternalLinkDialog({ open: false, position: { top: 0, left: 0 } });
      },
      [],
    );

    const handleExternalLinkCancel = useCallback(() => {
      setExternalLinkDialog({ open: false, position: { top: 0, left: 0 } });
      editorInstanceRef.current?.chain().focus().run();
    }, []);

    const handleEditorClick = useCallback(
      (e: React.MouseEvent) => {
        const link = (e.target as HTMLElement).closest("a");
        if (!link) return;
        e.preventDefault();
        const href = link.getAttribute("href");
        if (href?.startsWith("/")) {
          navigate(href);
        } else if (href) {
          window.open(href, "_blank", "noopener,noreferrer");
        }
      },
      [navigate],
    );

    const extractMentions = useCallback((editor: Editor) => {
      const mentions: string[] = [];
      editor.state.doc.descendants((node) => {
        if (node.type.name === "mention" && node.attrs.id) {
          mentions.push(node.attrs.id);
        }
      });
      return [...new Set(mentions)];
    }, []);

    // Create Yjs collaboration extension using y-prosemirror with cursor awareness
    const YjsExtension = useMemo(() => {
      if (!ydoc || !provider) return null;
      const yXmlFragment = ydoc.getXmlFragment("prosemirror");
      return Extension.create({
        name: "yjs",
        addProseMirrorPlugins() {
          return [
            ySyncPlugin(yXmlFragment),
            yCursorPlugin(provider.awareness, {
              cursorBuilder: () => {
                const cursor = document.createElement("span");
                cursor.style.borderLeft = "2px solid hsl(38, 92%, 50%)";
                cursor.style.marginLeft = "-1px";
                cursor.style.position = "relative";
                cursor.style.pointerEvents = "none";
                return cursor;
              },
              selectionBuilder: () => ({
                style: "background-color: hsla(38, 92%, 50%, 0.15)",
              }),
            }),
            yUndoPlugin(),
          ];
        },
        addKeyboardShortcuts() {
          return {
            "Mod-z": () => {
              undo(this.editor.state);
              return true;
            },
            "Mod-y": () => {
              redo(this.editor.state);
              return true;
            },
            "Mod-Shift-z": () => {
              redo(this.editor.state);
              return true;
            },
          };
        },
      });
    }, [ydoc, provider]);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImageFiles = useCallback(async (files: FileList | File[]) => {
      if (!canUploadImagesRef.current) {
        toast.error("Images can be added when the document is online.");
        return;
      }

      const ed = editorInstanceRef.current;
      if (!ed) return;
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) continue;
        try {
          const url = await resizeAndUploadImage(file);
          ed.chain().focus().setImage({ src: url, alt: file.name }).run();
        } catch {
          toast.error("Failed to upload image");
        }
      }
    }, []);

    const editor = useEditor(
      {
        extensions: [
          StarterKit.configure({
            heading: false,
            codeBlock: false,
            blockquote: false,
            horizontalRule: false,
            // Disable built-in history — yUndoPlugin handles undo/redo for Yjs
            undoRedo: false,
            link: {
              openOnClick: false,
              HTMLAttributes: {
                class: "text-primary underline cursor-pointer",
              },
            },
          }),
          Heading.configure({ levels: [1, 2, 3] }),
          ResizableImage,
          CodeBlock.configure({
            HTMLAttributes: {
              class: "bg-muted rounded-md p-4 font-mono text-sm",
            },
          }),
          Blockquote.configure({
            HTMLAttributes: {
              class:
                "border-l-4 border-primary/30 pl-4 italic text-muted-foreground",
            },
          }),
          HorizontalRule.configure({
            HTMLAttributes: { class: "my-4 border-t border-border" },
          }),
          TaskList.configure({ HTMLAttributes: { class: "not-prose pl-0" } }),
          TaskItem.configure({
            HTMLAttributes: { class: "flex items-start gap-2" },
            nested: true,
          }),
          Table.configure({
            resizable: true,
            handleWidth: 8,
            HTMLAttributes: {
              class: "border-collapse table-fixed w-full",
            },
          }),
          TableRow,
          TableHeader.configure({
            HTMLAttributes: {
              class:
                "border border-border bg-muted px-3 py-2 text-left font-semibold",
            },
          }),
          TableCell.configure({
            HTMLAttributes: {
              class: "border border-border px-3 py-2",
            },
          }),
          Placeholder.configure({ placeholder }),
          ...(YjsExtension ? [YjsExtension] : []),
          StableMention.configure({
            HTMLAttributes: {
              class: "mention bg-primary/20 text-primary rounded px-1 py-0.5",
            },
            suggestions: [
              {
                char: "@",
                items: ({ query }: { query: string }) =>
                  peopleRef.current
                    .filter((p) =>
                      `${p.first_name} ${p.last_name}`
                        .toLowerCase()
                        .includes(query.toLowerCase()),
                    )
                    .slice(0, 5)
                    .map((p) => ({
                      id: p.id,
                      label: `${p.first_name} ${p.last_name}`,
                      mentionID: generateUUID(),
                    })),
                render: () => {
                  let component: HTMLDivElement | null = null;
                  let selectedIndex = 0;
                  let items: MentionItem[] = [];
                  let executeCommand: ((item: MentionItem) => void) | null =
                    null;

                  const updateContent = () => {
                    if (!component) return;
                    component.innerHTML =
                      items.length === 0
                        ? '<div class="px-2 py-1 text-muted-foreground text-sm">No results</div>'
                        : items
                            .map(
                              (item, i) =>
                                `<div class="px-2 py-1 rounded cursor-pointer text-sm ${i === selectedIndex ? "bg-accent" : "hover:bg-accent/50"}" data-index="${i}">${item.label}</div>`,
                            )
                            .join("");
                  };

                  return {
                    onStart: (props: MentionSuggestionProps["lifecycle"]) => {
                      executeCommand = props.command;
                      items = props.items;
                      selectedIndex = 0;
                      component = document.createElement("div");
                      component.className =
                        "bg-popover border border-border rounded-md shadow-lg p-1 z-50";
                      updateContent();
                      component.addEventListener("click", (e) => {
                        const idx = (e.target as HTMLElement).dataset.index;
                        if (idx !== undefined)
                          props.command(items[parseInt(idx, 10)]);
                      });
                      const rect = props.clientRect?.();
                      if (rect && component) {
                        component.style.position = "fixed";
                        component.style.left = `${rect.left}px`;
                        component.style.top = `${rect.bottom + 4}px`;
                        document.body.appendChild(component);
                      }
                    },
                    onUpdate: (props: MentionSuggestionProps["lifecycle"]) => {
                      executeCommand = props.command;
                      items = props.items;
                      selectedIndex = 0;
                      updateContent();
                    },
                    onKeyDown: (props: MentionSuggestionProps["keyDown"]) => {
                      if (props.event.key === "ArrowUp") {
                        selectedIndex =
                          (selectedIndex - 1 + items.length) % items.length;
                        updateContent();
                        return true;
                      }
                      if (props.event.key === "ArrowDown") {
                        selectedIndex = (selectedIndex + 1) % items.length;
                        updateContent();
                        return true;
                      }
                      if (props.event.key === "Enter") {
                        if (items[selectedIndex])
                          executeCommand?.(items[selectedIndex]);
                        return true;
                      }
                      return false;
                    },
                    onExit: () => {
                      component?.remove();
                      component = null;
                      executeCommand = null;
                    },
                  };
                },
              },
              createSlashCommandSuggestion(slashItemsRef, handleExternalLink),
            ],
          }),
        ],
        editable: true,
        editorProps: {
          handleDrop: (_view, event, _slice, moved) => {
            if (moved || !event.dataTransfer?.files?.length) return false;
            const images = Array.from(event.dataTransfer.files).filter((f) =>
              f.type.startsWith("image/"),
            );
            if (images.length === 0) return false;
            event.preventDefault();
            handleImageFiles(images);
            return true;
          },
          handlePaste: (_view, event) => {
            const items = event.clipboardData?.items;
            if (!items) return false;
            const images: File[] = [];
            for (const item of Array.from(items)) {
              if (item.type.startsWith("image/")) {
                const file = item.getAsFile();
                if (file) images.push(file);
              }
            }
            if (images.length === 0) return false;
            event.preventDefault();
            handleImageFiles(images);
            return true;
          },
          attributes: {
            class: cn(
              "prose prose-sm prose-invert max-w-none focus:outline-none min-h-[150px] p-3",
              "prose-p:my-1 prose-ul:my-1 prose-ol:my-1",
              "prose-h1:text-xl prose-h1:font-bold prose-h1:my-2",
              "prose-h2:text-lg prose-h2:font-semibold prose-h2:my-2",
              "prose-h3:text-base prose-h3:font-medium prose-h3:my-1",
              "[&_.mention]:bg-primary/20 [&_.mention]:text-primary [&_.mention]:rounded [&_.mention]:px-1 [&_.mention]:py-0.5",
              "[&_ul[data-type=taskList]]:list-none [&_ul[data-type=taskList]]:pl-0",
              "[&_ul[data-type=taskList]_li]:flex [&_ul[data-type=taskList]_li]:items-start [&_ul[data-type=taskList]_li]:gap-2",
              "[&_ul[data-type=taskList]_input]:mt-1",
              "[&_a]:text-primary [&_a]:underline [&_a]:cursor-pointer",
              "[&.resize-cursor]:cursor-col-resize [&_.tableWrapper]:overflow-x-auto",
              "[&_table]:border-collapse [&_table]:table-fixed [&_table]:w-full [&_table]:overflow-hidden",
              "[&_th]:relative [&_th]:box-border [&_th]:align-top [&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:break-words",
              "[&_td]:relative [&_td]:box-border [&_td]:align-top [&_td]:border [&_td]:border-border [&_td]:px-3 [&_td]:py-2 [&_td]:break-words",
              "[&_.column-resize-handle]:pointer-events-none [&_.column-resize-handle]:absolute [&_.column-resize-handle]:bottom-0 [&_.column-resize-handle]:right-[-2px] [&_.column-resize-handle]:top-0 [&_.column-resize-handle]:z-20 [&_.column-resize-handle]:w-1 [&_.column-resize-handle]:bg-primary",
              "[&_.selectedCell]:after:pointer-events-none [&_.selectedCell]:after:absolute [&_.selectedCell]:after:inset-0 [&_.selectedCell]:after:z-10 [&_.selectedCell]:after:bg-primary/20 [&_.selectedCell]:after:content-['']",
            ),
            "data-gramm": "false",
            "data-gramm_editor": "false",
            "data-enable-grammarly": "false",
            inputmode: "text",
            enterkeyhint: "enter",
            autocomplete: "off",
            autocorrect: "on",
            autocapitalize: "sentences",
            spellcheck: "true",
            contenteditable: "true",
          },
        },
        onUpdate: ({ editor }) => {
          const mentions = extractMentions(editor);
          setMentionedPersonIds(mentions);
          onMentionsChange?.(mentions);
        },
      },
      [YjsExtension],
    );

    useEffect(() => {
      editorInstanceRef.current = editor;
    }, [editor]);

    useEffect(() => {
      if (editor) editor.setEditable(editable && canEdit);
    }, [editor, editable, canEdit]);

    useImperativeHandle(ref, () => ({
      getMentionedPersonIds: () => mentionedPersonIds,
    }));

    const triggerImageUpload = useCallback(() => {
      if (!canUploadImages) {
        toast.error("Images can be added when the document is online.");
        return;
      }
      fileInputRef.current?.click();
    }, [canUploadImages]);

    const handleFileInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
          handleImageFiles(Array.from(e.target.files));
          e.target.value = "";
        }
      },
      [handleImageFiles],
    );

    const syncStatus = isSynced
      ? {
          label: "Synced",
          className: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600",
        }
      : connectionStatus === "connected"
        ? {
            label: "Syncing",
            className: "border-amber-500/40 bg-amber-500/10 text-amber-600",
          }
        : {
            label: "Saved locally",
            className: "border-border bg-muted text-muted-foreground",
          };

    if (isLoading)
      return (
        <div
          className={cn(
            "border border-input rounded-md bg-background flex items-center justify-center min-h-[200px]",
            className,
          )}
        >
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    if (!documentId)
      return (
        <div
          className={cn(
            "border border-input rounded-md bg-background flex items-center justify-center min-h-[200px] text-muted-foreground",
            className,
          )}
        >
          No document available
        </div>
      );
    if (!canEdit)
      return (
        <div
          className={cn(
            "border border-input rounded-md bg-background flex flex-col items-center justify-center gap-2 min-h-[200px] text-muted-foreground",
            className,
          )}
        >
          {!hasServerConfirmedDocument ? (
            <span className="text-sm">
              Open this document online before editing offline.
            </span>
          ) : (
            <>
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-sm">Loading document from server...</span>
            </>
          )}
        </div>
      );

    return (
      <>
        <div
          className={cn(
            "border border-input rounded-md bg-background relative",
            className,
          )}
          onClick={handleEditorClick}
        >
          {editable && (
            <>
              <MenuBar
                editor={editor}
                onImageUpload={triggerImageUpload}
                canUploadImages={canUploadImages}
                syncStatus={syncStatus}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileInputChange}
              />
            </>
          )}
          <EditorContent editor={editor} />
          {!editable && (
            <div
              className={cn(
                "pointer-events-none absolute right-2 top-2 rounded border px-2 py-0.5 text-xs font-medium",
                syncStatus.className,
              )}
            >
              {syncStatus.label}
            </div>
          )}
        </div>
        <ExternalLinkDialog
          open={externalLinkDialog.open}
          position={externalLinkDialog.position}
          onSubmit={handleExternalLinkSubmit}
          onCancel={handleExternalLinkCancel}
        />
      </>
    );
  },
);

CollaborativeEditor.displayName = "CollaborativeEditor";
